import type { AppCategoryId } from '../pages/home/appStoreVocabulary';

/**
 * Everything kept from the GP Hub home page (growthplatformshub.deloitte.com/home),
 * reviewed item by item on 2026-09-30 (spec 2026-09-30-gp-hub-replacement §2, ADR 0029).
 *
 * TOOLS render as the launcher's "Growth Platforms tools" section, and PANEL_LINKS
 * render in the shell's links panel. Array order is display order.
 *
 * This is a checked-in list on purpose, not registry entries: the registry means
 * "apps", and the side nav, launcher grid, App Inventory, onboarding and AI settings
 * all read it. Changing a link is a small PR. growthPlatformsLinks.test.ts checks
 * the URLs, ids, categories and blurb lengths.
 */

export type ToolGroupId = 'growth-platforms' | 'all-mbrs';
export type LinkGroupId = 'growth-platforms' | 'other';

export interface Tool {
  id: string;
  name: string;
  /** Absolute https:// URL. A tool always opens in a new tab. */
  href: string;
  category: AppCategoryId;
  /** At most 90 characters, the same limit as a registry tagline. */
  blurb: string;
  group: ToolGroupId;
}

export interface PanelLink {
  id: string;
  name: string;
  /** Absolute https:// URL, opened in a new tab. */
  href: string;
  group: LinkGroupId;
  /** Shown in an info popover next to the link. */
  note?: string | undefined;
}

export const TOOL_GROUPS: readonly { id: ToolGroupId; title: string; subtitle: string }[] = [
  {
    id: 'growth-platforms',
    title: 'Growth Platforms',
    subtitle: 'Performance tracking and technology for alliances and tech relationships. Access is restricted.',
  },
  { id: 'all-mbrs', title: 'All MBRs', subtitle: 'Resources and technology open to all MBRs.' },
];

export const LINK_GROUPS: readonly { id: LinkGroupId; title: string }[] = [
  { id: 'growth-platforms', title: 'Growth Platforms' },
  { id: 'other', title: 'Other helpful links' },
];

export const TOOLS: readonly Tool[] = [
  {
    id: 'gps-crma',
    name: 'GPS CRMA',
    href: 'https://deloittecrmgps.lightning.force.com/analytics/dashboard/0FKap000000054XGAQ',
    category: 'insights',
    blurb: 'Unmasked sales and pipeline view. GPS-aligned professionals only.',
    group: 'growth-platforms',
  },
  {
    id: 'crma',
    name: 'CRMA',
    href: 'https://deloitte.lightning.force.com/analytics/dashboard/0FK7S000000MB5rWAG',
    category: 'insights',
    blurb: 'Sales and pipeline against targets, across all networks.',
    group: 'growth-platforms',
  },
  {
    id: 'lap-trainings',
    name: 'Lead Alliance Partner Trainings',
    href: 'https://sabacloud.deloitteresources.com/Saba/Web_spf/E103PRD0001/app/me/ledetail/curra000000000050364;spf-url=common%2flearningeventdetail%2fcurra000000000050364',
    category: 'search',
    blurb: 'Onboarding trainings for Lead Alliance Partners (LAPs).',
    group: 'growth-platforms',
  },
  {
    id: 'annual-planning',
    name: 'Annual Planning Module',
    href: 'https://app.powerbi.com/groups/8da584fd-730a-4205-83a2-45e859f21e96/reports/0c974341-dd67-49de-899c-ae87488de31f?ctid=36da45f1-dd2c-4d1f-af13-5abe46b99921&pbi_source=linkShare',
    category: 'strategy',
    blurb: 'Plan and run the yearly budget and strategic planning process.',
    group: 'growth-platforms',
  },
  // Added 2026-10-29, not from the Hub: the Dataiku alliance's sandbox access form.
  // The sandbox itself is hosted on Dataiku's cloud; owner is the Dataiku alliance manager.
  {
    id: 'dataiku-sandbox',
    name: 'Dataiku Sandbox',
    href: 'https://forms.cloud.microsoft/Pages/ResponsePage.aspx?id=8UXaNizdH02vE1q-RrmZIQN8mrDFFrNBvA0OAGHzrrFUN0pZOTNMUldPVVkySUdJM1VVMVVBTkJZMi4u',
    category: 'search',
    blurb: 'Request access to the Dataiku Cloud Sandbox for a three-month period.',
    group: 'growth-platforms',
  },
  {
    id: 'priority-relationships',
    name: 'Growth Platforms & Priority Relationships',
    href: 'https://mbrinsights.deloitte.com/mbrs',
    category: 'search',
    blurb: 'Priority third-party relationships across alliances, tech and MBR entities.',
    group: 'all-mbrs',
  },
  {
    id: 'mbr-insights',
    name: 'MBR Insights',
    href: 'https://mbrinsights.deloitte.com/',
    category: 'insights',
    blurb: "Deloitte's full relationship with a third party: sell-to, sell-with and buy-from.",
    group: 'all-mbrs',
  },
  {
    id: 'gps-jupiter',
    name: 'GPS Jupiter',
    href: 'https://deloittecrmgps.lightning.force.com/lightning/page/home',
    category: 'search',
    blurb: "Deloitte's Salesforce CRM, for GPS-aligned professionals.",
    group: 'all-mbrs',
  },
  {
    id: 'jupiter',
    name: 'Jupiter',
    href: 'https://deloitte.lightning.force.com/lightning/page/home',
    category: 'search',
    blurb: "Deloitte's global Salesforce CRM.",
    group: 'all-mbrs',
  },
  {
    id: 'zenith',
    name: 'Zenith Investment Management',
    href: 'https://deloittezenith.lightning.force.com/lightning/page/home',
    category: 'strategy',
    blurb: 'Request investment in Growth Platforms alliances and tech relationships.',
    group: 'all-mbrs',
  },
];

export const PANEL_LINKS: readonly PanelLink[] = [
  {
    id: 'internal-investment-dashboard',
    name: 'Internal Investment Dashboard',
    href: 'https://app.powerbi.com/groups/me/apps/e64bb294-dc4f-4d62-bd00-dd8668013ed8/reports/7750f10f-b824-4b89-bde0-4d27c0e18e0a?ctid=36da45f1-dd2c-4d1f-af13-5abe46b99921&experience=power-bi',
    group: 'growth-platforms',
  },
  {
    id: 'dnet',
    name: 'DNet Site',
    href: 'https://resources.deloitte.com/sites/global/Services/alliances/Pages/Home.aspx',
    group: 'growth-platforms',
  },
  {
    id: 'teams',
    name: 'Microsoft Teams Site',
    href: 'https://teams.microsoft.com/l/team/19%3a22224ce191864ba88b31c58bfc2dfa09%40thread.skype/conversations?groupId=3c0ca043-4c41-4b8f-bcf2-b7fb29cec2d6&tenantId=36da45f1-dd2c-4d1f-af13-5abe46b99921',
    group: 'growth-platforms',
  },
  {
    id: 'management-roster',
    name: 'Management Roster',
    href: 'https://airtable.com/app2plkZH0FYsHE0F/shrvhC50fX7nEogf0/tbliye7UnkLKwSUAs',
    group: 'growth-platforms',
    note: 'View-only link for Deloitte users. Sign in to AirTable, or create a free account, with your Deloitte email to confirm access. For access help, contact GP Technology.',
  },
  {
    id: 'shared-services',
    name: 'Shared Services Site',
    href: 'https://resources.deloitte.com/sites/global/Services/Pages/GP_Services.aspx',
    group: 'growth-platforms',
  },
  {
    id: 'stump-deck',
    name: 'E&A Stump Deck 101',
    href: 'https://resources.deloitte.com/:f:/r/sites/USEACommunitySite/Shared%20Documents/General/01%20-%20Education%20and%20Reference%20Materials/01%20-%20E%26A%20101%20Stump%20Decks?csf=1&web=1&e=wEPG4o',
    group: 'growth-platforms',
  },
  {
    id: 'alliances-kx',
    name: 'Alliances KX',
    href: 'https://resources.deloitte.com/sites/global/Services/alliances/Pages/FindAlliance.aspx',
    group: 'growth-platforms',
  },
  {
    id: 'tech-relationships-kx',
    name: 'Tech Relationships KX',
    href: 'https://resources.deloitte.com/sites/global/Services/techrelationships/Pages/home.aspx',
    group: 'growth-platforms',
  },
  { id: 'gia', name: 'GIA | Growth Insights & Activation', href: 'https://giaplatform.deloitte.com/', group: 'other' },
  { id: 'commercial-sidekick', name: 'Commercial Sidekick', href: 'https://sidekick.deloitte.com/', group: 'other' },
  {
    id: 'gps-market-insights',
    name: 'GPS Market & Insights',
    href: 'https://app.powerbi.com/groups/me/apps/079f5a76-dec7-4582-87a1-a76c602247a0/reports/5ca0a2ec-4474-491d-aea8-f219485c50a0?ctid=36da45f1-dd2c-4d1f-af13-5abe46b99921&experience=power-bi&clientSideAuth=0',
    group: 'other',
  },
  { id: 'gps-sidekick', name: 'GPS Sidekick', href: 'https://sidekick.gps.deloitte.com/', group: 'other' },
  {
    id: 'tprm',
    name: 'TPRM | Third Party Risk Management',
    href: 'https://ssodeloitteprod.aravo.com/aems/aravo-js/public/#/app/930001/dashboard/421307',
    group: 'other',
  },
  { id: 'relay', name: 'Relay', href: 'https://relay.deloitte.com/', group: 'other' },
];

/** The links panel's own labels. Kept here (no React) so the vocabulary guard can check them. */
export const LINKS_COPY = {
  title: 'Growth Platforms Links',
  drawerName: 'Growth Platforms Links',
  triggerButton: 'Open Growth Platforms Links',
  closeButton: 'Close Growth Platforms Links',
  /** The home page's labeled button for the panel (ADR 0044). Elsewhere it's the icon alone. */
  homeButton: 'Helpful External Links',
  /** Appended to each external link's accessible name. */
  newTab: '(opens in a new tab)',
  /** The info button's label: "About Management Roster". */
  aboutPrefix: 'About',
  /** The access-note popover's dismiss button, so a keyboard user can close it. */
  closeNote: 'Close note',
} as const;
